import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, CircleDashed, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { StatusBadge } from "@/components/StatusBits";
import { api, errMsg, fmtDT } from "@/lib/api";

const STATE = {
  free: { tone: "success", icon: CheckCircle2, hint: "Recommended — nothing else uses this number." },
  this_site: { tone: "info", icon: CheckCircle2, hint: "Already set up for Phone Blower." },
  in_use_elsewhere: { tone: "warning", icon: AlertTriangle, hint: "Another app or site handles this number." },
};
const OWNER_DOT = { other: "bg-amber-500", this_site: "bg-blue-500", none: "bg-slate-300" };

const Usage = ({ n }) => (
  <ul className="space-y-1" data-testid={`number-usage-${n.sid}`}>
    {(n.usage || []).map((u, i) => (
      <li key={i} className="flex items-start gap-2 text-xs text-slate-600">
        <span className={`mt-1.5 h-1.5 w-1.5 rounded-full shrink-0 ${OWNER_DOT[u.owner]}`} />
        <span>{u.text}{u.url && <span className="block font-mono text-[10px] text-slate-400 break-all">{u.url}</span>}</span>
      </li>
    ))}
    <li className="text-[11px] text-slate-400">Capabilities: {Object.entries(n.capabilities || {}).filter(([, v]) => v).map(([k]) => k.toUpperCase()).join(", ") || "—"}</li>
  </ul>
);

const confirmText = (c) => {
  if (!c) return "";
  const elsewhere = c.n.usage_state === "in_use_elsewhere";
  const where = (c.n.usage || []).filter((u) => u.owner === "other").map((u) => u.target).join(", ");
  if (c.kind === "enable") {
    return `${c.n.phone_number} is currently used by ${where}. Using it as caller ID here does not change that setup, but when customers call or text it back, they will reach ${where} — not Phone Blower.`;
  }
  return `${c.n.phone_number} will be unlinked from ${where || "its current setup"} and used here: inbound calls and texts come to Phone Blower and it becomes available as caller ID.${elsewhere ? ` ${where} will STOP receiving its calls and texts.` : ""} Its previous settings are saved so you can relink it later.`;
};

const whereOf = (usage) => [...new Set((usage || []).filter((u) => u.owner === "other").map((u) => u.target))].join(", ");

export const NumbersSection = () => {
  const [nums, setNums] = useState([]);
  const [confirm, setConfirm] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const load = useCallback(() => api.get("/numbers").then((r) => setNums(r.data)).catch(() => {}), []);
  const sync = useCallback(async (quiet) => {
    setSyncing(true);
    try {
      setNums((await api.post("/numbers/sync")).data);
      if (!quiet) toast.success("Numbers refreshed from Twilio");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSyncing(false);
    }
  }, []);
  useEffect(() => {
    load();
    sync(true);
  }, [load, sync]);
  const act = async (fn, msg) => {
    try {
      await fn();
      if (msg) toast.success(msg);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const toggle = (n, v) => (v && n.usage_state === "in_use_elsewhere" ? setConfirm({ kind: "enable", n }) : act(() => api.put(`/numbers/${n.sid}`, { enabled: v })));
  const run = () => {
    const c = confirm;
    setConfirm(null);
    if (c.kind === "enable") act(() => api.put(`/numbers/${c.n.sid}`, { enabled: true }), "Caller ID enabled");
    else act(() => api.post(`/numbers/${c.n.sid}/route-here`), "Unlinked and now used here");
  };
  const sorted = [...nums].sort((a, b) => ["free", "this_site", "in_use_elsewhere"].indexOf(a.usage_state) - ["free", "this_site", "in_use_elsewhere"].indexOf(b.usage_state));

  return (
    <div className="space-y-3" data-testid="numbers-section">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium">Your Twilio numbers ({nums.length})</p>
        <Button size="sm" variant="outline" data-testid="numbers-sync-btn" disabled={syncing} onClick={() => sync(false)}><RefreshCw className={`h-3.5 w-3.5 mr-1 ${syncing ? "animate-spin" : ""}`} />{syncing ? "Checking…" : "Refresh"}</Button>
      </div>
      <p className="text-[11px] text-slate-500">All numbers on your Twilio account load here automatically, with where each one is linked right now. You decide per number: <b>Unlink & use here</b>, use it only as outgoing caller ID (keeps its current link), or leave it alone. Twilio can't show which other apps use a number only for outgoing caller ID.</p>
      {sorted.map((n) => {
        const S = STATE[n.usage_state] || STATE.free;
        return (
          <div key={n.sid} className="border border-slate-200 rounded-md p-3 space-y-2" data-testid={`number-row-${n.sid}`}>
            <div className="flex flex-wrap items-center gap-2">
              <S.icon className={`h-4 w-4 ${n.usage_state === "in_use_elsewhere" ? "text-amber-600" : "text-emerald-600"}`} />
              <span className="font-mono text-sm">{n.phone_number}</span>
              <span className="text-xs text-slate-500">{n.friendly_name}</span>
              <StatusBadge tone={S.tone} value="x" testId={`number-state-${n.sid}`}>{n.usage_summary || "Not checked"}</StatusBadge>
              <label className="flex items-center gap-1.5 text-xs ml-auto">{n.usage_state === "in_use_elsewhere" ? "Outgoing caller ID only" : "Use as caller ID"}
                <Switch data-testid={`number-enable-${n.sid}`} checked={!!n.enabled} onCheckedChange={(v) => toggle(n, v)} />
              </label>
            </div>
            {n.usage_state === "in_use_elsewhere" && <p className="text-xs font-medium text-amber-800" data-testid={`number-linked-${n.sid}`}>Linked to: {whereOf(n.usage)}</p>}
            <p className="text-[11px] text-slate-500">{S.hint}</p>
            <Usage n={n} />
            <div className="flex flex-wrap items-center gap-2">
              {n.previous ? (
                <Button size="sm" variant="ghost" data-testid={`number-restore-${n.sid}`} onClick={() => act(() => api.post(`/numbers/${n.sid}/restore`), "Relinked to its previous setup")}>
                  {whereOf(n.previous_usage) ? `Relink to ${whereOf(n.previous_usage)}` : "Restore previous settings"}
                </Button>
              ) : !n.routed_here && (
                <Button size="sm" variant={n.usage_state === "in_use_elsewhere" ? "default" : "outline"} data-testid={`number-route-${n.sid}`} onClick={() => setConfirm({ kind: "route", n })}>
                  {n.usage_state === "in_use_elsewhere" ? "Unlink & use here" : "Use here"}
                </Button>
              )}
              <span className="text-[10px] text-slate-400 ml-auto">Checked {fmtDT(n.synced_at)}</span>
            </div>
          </div>
        );
      })}
      {!nums.length && <p className="text-xs text-slate-500 flex items-center gap-1"><CircleDashed className="h-3.5 w-3.5" />No numbers yet{syncing ? " — loading from Twilio…" : " on this Twilio account."}</p>}
      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent className="bg-white" data-testid="number-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm?.kind === "enable" ? "Use only as outgoing caller ID?" : `Unlink ${confirm?.n.phone_number} and use it here?`}</AlertDialogTitle>
            <AlertDialogDescription>{confirmText(confirm)}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="number-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="number-confirm-ok" onClick={run}>{confirm?.kind === "enable" ? "Use as caller ID, keep link" : "Unlink & use here"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
